'use client';

import { useState } from 'react';
import { PortableTextBlock } from '@portabletext/react';
import type { Breakpoint } from '@mui/material/styles';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import useTranslations from '@/hooks/useTranslations';
import CardWithImage from './CardWithImage';
import type { OutcomeChip } from './OutcomeChips';
import ShowDescription from './ShowDescription';
import { Loading } from './Loading';
import Description from './Description';
import SmallFavourite from './SmallFavourite';
import Favourite from './Favourite';
import useAuth from '@/lib/firebase/auth/store';
import { ADMIN_ROLE } from '@/constants/roles';
import { workflowStore } from '../workflow/workflowStore';
import { WORKFLOW_STEP_OUTCOME_ID } from '@/constants/global';
import CompanyFavourite from './CompanyFavourite';
import { StarButton } from './StarButton';
import { ProductConfiguration } from '@/types/productConfiguration';
import { Link } from '@/blocks/components/types';
import { WorkflowCategories } from '@/types/fragments';
import type { DecisionDialogAnswer } from '@/types/decisionFlow';

interface CardItemProps {
  id: string;
  title: string;
  alternativeTitle?: string;
  subtitle?: string;
  link?: Link | string | null;
  description?: PortableTextBlock[];
  image: string;
  basePath?: string;
  selected?: boolean;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  size?: {
    [key in Breakpoint]?: number;
  };
  withDescription?: boolean;
  showFavourite?: boolean;
  showSmallFavourite?: boolean;
  showDiscoverButton?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  isCompanyFavourite?: boolean;
  hasAlreadyBeenFavourited?: boolean;
  hasAlreadyBeenCompanyFavourited?: ProductConfiguration;
  product?: WorkflowCategories;
  /** Shown above the title on the outcome card. */
  chips?: OutcomeChip[];
  /** The outcome's dialog answers a favourite made from it keeps. */
  favouriteAnswers?: Record<string, DecisionDialogAnswer>;
}

export default function CardWithDetails({
  id,
  title,
  alternativeTitle,
  subtitle,
  link,
  description,
  image,
  basePath,
  selected,
  objectFit = 'contain',
  size = { xs: 8, sm: 4, md: 2 },
  withDescription = false,
  showFavourite = false,
  showSmallFavourite = false,
  showDiscoverButton = true,
  onClick,
  disabled,
  loading,
  isCompanyFavourite,
  hasAlreadyBeenFavourited,
  hasAlreadyBeenCompanyFavourited,
  product,
  chips,
  favouriteAnswers,
}: CardItemProps) {
  const [showDescription, setShowDescription] = useState(false);
  const { t } = useTranslations();
  const viewItemLabel = t('cards.viewItem', { name: title });
  const cardLink = link || `${basePath}/${id}`;
  const { roles } = useAuth();
  const isAdmin = roles.includes(ADMIN_ROLE);
  const { currentStep } = workflowStore();
  const isLastStep = currentStep?.id === WORKFLOW_STEP_OUTCOME_ID;

  return (
    <Grid size={size}>
      <Card
        sx={{
          p: 0,
          position: 'relative',
          border: '2px solid',
          borderColor: selected ? 'primary.main' : 'transparent',
          '&:hover': {
            borderColor: loading ? 'transparent' : 'primary.light',
          },
          height: '100%',
          bgcolor: 'background.default',
          color: 'text.primary',
          transition: 'height 0.5s ease-in-out',
          aspectRatio: '1/1',
          opacity: loading ? 0.9 : 1,
        }}
      >
        {!showDescription ? (
          <CardWithImage
            title={title}
            alternativeTitle={alternativeTitle}
            onClick={onClick}
            link={link}
            cardLink={cardLink}
            viewItemLabel={viewItemLabel}
            disabled={disabled}
            loading={loading}
            objectFit={objectFit}
            image={image}
            subtitle={subtitle}
            showDiscoverButton={showDiscoverButton}
            chips={chips}
          />
        ) : (
          <ShowDescription
            title={title}
            subtitle={subtitle}
            description={description}
            viewItemLabel={viewItemLabel}
            id={id}
          />
        )}

        {loading && <Loading />}

        {withDescription && (
          <Description
            setShowDescription={setShowDescription}
            showDescription={showDescription}
            loading={loading}
          />
        )}

        {showFavourite && (
          <>
            {/* OUTCOME PAGE - Show on left, initially inactive */}
            {isAdmin && isLastStep && (
              <CompanyFavourite
                existingConfigId={id}
                hasAlreadyBeenCompanyFavourited={hasAlreadyBeenCompanyFavourited}
                isOutcomePage={true}
                answers={favouriteAnswers}
              />
            )}

            {!withDescription && (
              <>
                {/* FIRST PAGE - Show on right if is company favourite */}
                {isCompanyFavourite ? (
                  isAdmin ? (
                    <CompanyFavourite
                      existingConfigId={id}
                      hasAlreadyBeenCompanyFavourited={hasAlreadyBeenCompanyFavourited}
                      isOutcomePage={false}
                      product={product}
                    />
                  ) : (
                    <StarButton
                      leftPosition={false}
                      border={true}
                      backgroundColor={true}
                      isFavourited={false}
                      ariaLabel={t('cards.favourite')}
                      disabled={true}
                      smallStar={true}
                      text={t('product.workflow.companyFavourite.title')}
                    />
                  )
                ) : showSmallFavourite ? (
                  <SmallFavourite id={id} product={product} />
                ) : (
                  <Favourite
                    title={title}
                    subtitle={subtitle}
                    loading={loading}
                    existingCarpiProductConfigurationId={id}
                    hasAlreadyBeenFavourited={hasAlreadyBeenFavourited}
                    answers={favouriteAnswers}
                  />
                )}
              </>
            )}
          </>
        )}
      </Card>
    </Grid>
  );
}
